import { useState, useEffect, useCallback, useRef } from 'react';
import { Editor } from '@tiptap/react';
import { nanoid } from 'nanoid';
import { Node } from '@tiptap/pm/model';
import {
  onUploadRegistryChange,
  isAnyImageUploadingFor,
  cancelAllImageUploads,
  clearAllPastedFiles,
  registerPastedFile
} from '../imageUploadEmbed';
import { CommentFilesInterface } from '../../../interfaces/TaskInterface';
import { DEFAULT_UPLOAD_FOLDER } from '../../../helpers/common';
import { captureException } from '../../../services/logService';
import { dispatchAppError } from '../../../services/appService';
import history from '../../../history';

interface UseEditorUploadsParams {
  editor: Editor | null;
  isTaskDescriptionDropBox: boolean;
  setValueContent?: (value: string) => void;
  setLoading?: (value: boolean) => void;
  setStateLoading?: (value: boolean) => void;
  addUploadedFiles?: (files: CommentFilesInterface[]) => void;
  onUploadCompleted?: () => void;
  editorInstanceId?: string;
  /** Storage sub-folder for images uploaded from this editor. Defaults to `files/`. */
  uploadFolder?: string;
}

export function useEditorUploads({
  editor,
  isTaskDescriptionDropBox,
  setValueContent,
  setLoading,
  setStateLoading,
  addUploadedFiles,
  onUploadCompleted,
  editorInstanceId,
  uploadFolder = DEFAULT_UPLOAD_FOLDER
}: UseEditorUploadsParams) {
  const [isUploadBlockingModalOpen, setIsUploadBlockingModalOpen] = useState(false);
  const [isAnyUploadInProgress, setIsAnyUploadInProgress] = useState<boolean>(false);
  const [isEditorFocused, setIsEditorFocused] = useState<boolean>(false);
  const editorInstanceIdRef = useRef<string>(editorInstanceId || nanoid());
  const pendingTransitionRef = useRef<{
    pathname: string;
    search: string;
    hash: string;
    action: 'PUSH' | 'REPLACE' | 'POP';
  } | null>(null);
  const unblockRef = useRef<null | (() => void)>(null);
  const prevUploadInProgressRef = useRef(false);

  const isScoped = true;

  const checkUploading = useCallback(() => {
    return isAnyImageUploadingFor(editorInstanceIdRef.current);
  }, []);

  // Track upload status from image upload registry
  useEffect(() => {
    const unsubscribe = onUploadRegistryChange(() => {
      setIsAnyUploadInProgress(checkUploading());
    });
    setIsAnyUploadInProgress(checkUploading());
    return unsubscribe;
  }, [checkUploading]);

  // Notify parent when upload completes (scoped version only)
  useEffect(() => {
    if (!onUploadCompleted) return;
    let prevUploading = isAnyImageUploadingFor(editorInstanceIdRef.current);
    const unsubscribe = onUploadRegistryChange(() => {
      const currentUploading = isAnyImageUploadingFor(editorInstanceIdRef.current);
      if (prevUploading && !currentUploading) {
        onUploadCompleted?.();
      }
      prevUploading = currentUploading;
    });
    return () => {
      unsubscribe();
    };
  }, [onUploadCompleted]);

  // Block SPA navigation when uploading in task description editor only
  useEffect(() => {
    if (!isTaskDescriptionDropBox) return;

    if (!unblockRef.current) {
      unblockRef.current = history.block((location: unknown, action: unknown) => {
        if (isAnyUploadInProgress && !isUploadBlockingModalOpen) {
          pendingTransitionRef.current = {
            pathname: (location as { pathname: string }).pathname,
            search: (location as { search?: string }).search || '',
            hash: (location as { hash?: string }).hash || '',
            action: (action as 'PUSH' | 'REPLACE' | 'POP') || 'PUSH'
          };
          setIsUploadBlockingModalOpen(true);
          return false;
        }
        return;
      });
    }

    return () => {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
    };
  }, [isAnyUploadInProgress, isUploadBlockingModalOpen, isTaskDescriptionDropBox]);

  // Warn on browser/tab close during upload
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isAnyUploadInProgress) {
        try {
          cancelAllImageUploads();
          clearAllPastedFiles();
        } catch {
          // ignore
        }
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isAnyUploadInProgress]);

  const triggerTaskUpdateAfterUpload = useCallback(() => {
    if (!editor) return;
    if (!isEditorFocused) {
      if (isTaskDescriptionDropBox && setValueContent) {
        const html = editor.getHTML();
        if (!html || html === '<p></p>') return;
        setValueContent(html);
      } else {
        editor.commands.blur();
      }
    }
  }, [editor, setValueContent, isEditorFocused, isTaskDescriptionDropBox]);

  // Upload just finished -> trigger update
  useEffect(() => {
    const prev = prevUploadInProgressRef.current;
    const current = isAnyUploadInProgress;
    if (prev && !current) {
      triggerTaskUpdateAfterUpload();
    }
    prevUploadInProgressRef.current = current;
  }, [isAnyUploadInProgress, triggerTaskUpdateAfterUpload]);

  const handleConfirmLeave = useCallback(() => {
    const tx = pendingTransitionRef.current;
    setIsUploadBlockingModalOpen(false);
    cancelAllImageUploads();
    clearAllPastedFiles();
    if (unblockRef.current) {
      const doUnblock = unblockRef.current;
      unblockRef.current = null;
      doUnblock();
    }
    if (tx && tx.pathname) {
      const path = `${tx.pathname}${tx.search || ''}${tx.hash || ''}`;
      if (tx.action === 'REPLACE') {
        history.replace(path);
      } else if (tx.action === 'POP') {
        history.goBack();
      } else {
        history.push(path);
      }
      pendingTransitionRef.current = null;
    } else {
      history.goBack();
    }
  }, []);

  const handleCancelLeave = useCallback(() => {
    setIsUploadBlockingModalOpen(false);
    pendingTransitionRef.current = null;
  }, []);

  // Convert file to base64
  const convertBase64 = useCallback((file: File) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        const item = {
          fileContent: fileReader.result,
          fileType: file?.type,
          name: file?.name,
          size: file?.size,
          file: file
        };
        resolve(item);
      };
      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  }, []);

  const handleFileRead = useCallback(
    async (file: File) => {
      if (file?.size > 2.5e8) {
        dispatchAppError('Please upload a file smaller than 250 MB.');
        return false;
      }
      const newPromise = convertBase64(file);
      try {
        const value = await Promise.all([newPromise]);
        return value[0];
      } catch (error) {
        captureException(error);
      }
    },
    [convertBase64]
  );

  // Add a completed image (already uploaded URL) into editor
  const addImageInContent = useCallback(
    (url: string) => {
      if (url && editor) {
        const uniqueId = nanoid();
        const { state } = editor.view;

        const imageNode = state.schema.nodes.imageUploadEmbed.create({
          nodeId: uniqueId,
          src: url,
          isUploading: false,
          progress: 100,
          uploadFolder,
          setLoading: (loading: boolean) => {
            if (setLoading) setLoading(loading);
            if (setStateLoading) setStateLoading(loading);
          }
        });

        if (imageNode) {
          let transaction = state.tr.replaceSelectionWith(imageNode);
          const endPos = transaction.doc.content.size;
          const emptyParagraph = state.schema.nodes.paragraph.create();
          transaction = transaction.insert(endPos, emptyParagraph);
          editor.view.dispatch(transaction);
          editor.commands.focus();
        }
      }
    },
    [editor, setLoading, setStateLoading, uploadFolder]
  );

  // Add image file(s) that need uploading into editor
  const addImageFileInContent = useCallback(
    async (files: File | File[]) => {
      if (!editor) return;

      const filesArray = Array.isArray(files) ? files : [files];
      if (filesArray.length === 0) return;

      const currentSetLoading = setLoading;
      const currentSetStateLoading = setStateLoading;

      const nodesToInsert: Node[] = [];
      const { state } = editor.view;

      for (let i = 0; i < filesArray.length; i++) {
        const file = filesArray[i];
        if (!file || !(file instanceof File)) {
          continue;
        }

        const blobUrl = URL.createObjectURL(file);
        const uniqueId = nanoid();
        registerPastedFile(uniqueId, file);

        const uploadingNode = state.schema.nodes.imageUploadEmbed?.create({
          nodeId: uniqueId,
          src: blobUrl,
          isUploading: true,
          progress: 0,
          uploadFolder,
          setLoading: (loading: boolean) => {
            if (currentSetLoading) currentSetLoading(loading);
            if (currentSetStateLoading) currentSetStateLoading(loading);
          }
        });

        if (uploadingNode) {
          nodesToInsert.push(uploadingNode);
        }
      }

      if (nodesToInsert.length > 0) {
        let transaction = state.tr.replaceSelectionWith(nodesToInsert[0]);

        for (let i = 1; i < nodesToInsert.length; i++) {
          const currentPos = transaction.selection.to;
          transaction = transaction.insert(currentPos, nodesToInsert[i]);
        }

        const endPos = transaction.doc.content.size;
        const emptyParagraph = state.schema.nodes.paragraph.create();
        transaction = transaction.insert(endPos, emptyParagraph);

        editor.view.dispatch(transaction);
        editor.commands.focus();
      }
    },
    [editor, setLoading, setStateLoading, uploadFolder]
  );

  return {
    isUploadBlockingModalOpen,
    isAnyUploadInProgress,
    isEditorFocused,
    setIsEditorFocused,
    editorInstanceIdRef,
    handleConfirmLeave,
    handleCancelLeave,
    handleFileRead,
    addImageInContent,
    addImageFileInContent,
    isScoped
  };
}
